import { Section } from "app/shared/ui";
import {
  Grid as MuiGrid,
  Tooltip as MuiTooltip,
  Typography as MuiTypography,
} from "@mui/material";
import isJSON from "is-json";
import { History as HistoryIcon } from "@mui/icons-material";
import { DataGridPro as MuiDataGridPro } from "@mui/x-data-grid-pro";
import DOMPurify from "dompurify";
import { FormSummaryRow } from "./FormSummaryRow";
import { FormStatus, AllowedTags } from "app/shared/constants";
import { replaceFieldCalculationValues } from "app/shared/utils";

const StyleApp = {
  summaryCell: {
    border: 0,
    padding: "8px 16px",
  },
  summaryLink: {
    "& a": {
      textDecoration: "none",
      color: "#007c92",
      cursor: "pointer",
    },
  },
  MuiDataGridStyles: {
    "& .MuiDataGrid-filler": {
      display: "none",
    },
    "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
      {
        outline: "none",
      },
  },
};

export const FormSummaryView = ({ summaryList, ...rest }) => {
  const {
    getFieldObject,
    allFields,
    t,
    setScrollDialog,
    summaryFormSections,
    formFlatMap,
    formik,
  } = rest;

  if (!summaryFormSections || summaryFormSections.length === 0) {
    return;
  }

  return summaryFormSections.map((section) => (
    <Section
      type="summary"
      key={section.field_id}
      label={section.field_label}
      fieldAttr={section.field_attributes}
      {...rest}
    >
      <MuiGrid container key={section.field_id}>
        {section.fields.map((item) => {
          const iconSize = Boolean(item?.updated) ? "large" : "small";
          const iconColor = Boolean(item?.updated) ? "error" : "inherit";
          const links = Boolean(item?._links?.self?.href)
            ? item?._links?.self?.href
            : "";
          const attributes =
            item.field_attributes && isJSON(item.field_attributes)
              ? JSON.parse(item.field_attributes)
              : null;
          const fieldType = attributes?.field_type;
          const fieldGroup = attributes?.field_group;
          const showEmptyFields = attributes?.showEmptyFields;
          const isDescriptive = attributes?.isDescriptive ?? false;
          const isTable = attributes?.isTable ?? false;
          const maxRows = attributes?.maxRows ?? 3;
          const column = attributes?.columnOrder ?? null;

          const columnOrder =
            attributes && column
              ? replaceFieldCalculationValues(column, {
                  ...formFlatMap,
                  ...formik.values,
                }).replace(/(^"|"$)/g, "")
              : {};

          const tableColumnHeader = isJSON(columnOrder)
            ? JSON.parse(columnOrder).map((item) => item.id)
            : [];

          if (isTable && item.field_value) {
            // parse table data
            const tableData = isJSON(item.field_value)
              ? JSON.parse(item.field_value)
              : [];
            // create rows (add `id` for DataGrid)
            const rows = Array.isArray(tableData)
              ? tableData.map((item, index) => ({
                  id: index + 1,
                  ...item,
                }))
              : [];
            // create dynamic column definitions
            const columns = tableData.length
              ? Object.keys(tableData[0])
                  .sort((a, b) => {
                    const aIndex = tableColumnHeader.indexOf(a);
                    const bIndex = tableColumnHeader.indexOf(b);

                    // keys not in preferredOrder go to the end
                    if (aIndex === -1 && bIndex === -1) return 0;
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;

                    return aIndex - bIndex;
                  })
                  .map((key) => ({
                    field: key,
                    headerName: key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase()),
                    flex: 1,
                  }))
              : [];

            return tableData.length > 0 ? (
              <MuiGrid
                size={11.5}
                key={item.field_id}
                sx={{
                  pl: 2,
                }}
              >
                <MuiGrid
                  sx={{
                    pb: 1,
                  }}
                >
                  <MuiTypography variant="body2">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.field_label, {
                          ALLOWED_TAGS: AllowedTags,
                        }),
                      }}
                    />
                  </MuiTypography>
                </MuiGrid>
                <MuiGrid>
                  <MuiDataGridPro
                    rows={rows}
                    columns={columns}
                    density="compact"
                    hideFooter
                    disableColumnMenu
                    disableColumnResize
                    disableColumnReorder
                    disableColumnSorting
                    disableRowSelectionOnClick
                    sx={StyleApp.MuiDataGridStyles}
                    pagination
                    initialState={{
                      pagination: { paginationModel: { pageSize: maxRows } },
                    }}
                  />
                </MuiGrid>
              </MuiGrid>
            ) : null;
          }

          if (Array.isArray(item.field_value) && Array.isArray(fieldGroup)) {
            const fields = fieldGroup.map((item) =>
              getFieldObject(item, allFields),
            );
            return item.field_value.map((fieldItem, index) => (
              <FormSummaryRow
                key={`${fieldItem.id}-${index}`}
                fields={fields}
                formValues={{
                  ...fieldItem.values,
                  action_id: fieldItem.id,
                  action_status: t(FormStatus[fieldItem.status]),
                }}
                formFields={allFields}
                showEmptyFields={showEmptyFields}
              />
            ));
          }

          return item.field_value && !isDescriptive ? (
            <MuiGrid container size={12} key={item.field_id}>
              <MuiGrid size={6} sx={StyleApp.summaryCell}>
                <MuiTypography variant="body2" sx={StyleApp.summaryLink}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.field_label, {
                        ALLOWED_TAGS: AllowedTags,
                      }),
                    }}
                  />
                </MuiTypography>
              </MuiGrid>
              <MuiGrid size={5} sx={StyleApp.summaryCell}>
                <MuiTypography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: "25.5px",
                    fontWeight: 600,
                  }}
                >
                  {getFormattedValue(item.field_value, fieldType)}
                </MuiTypography>
              </MuiGrid>
              {Boolean(links) && (
                <MuiGrid size={1} sx={StyleApp.summaryCell}>
                  <MuiTypography
                    variant="body1"
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    <MuiTooltip title={t("globals.fieldHistory.tooltip")}>
                      <HistoryIcon
                        fontSize={iconSize}
                        color={iconColor}
                        onClick={() =>
                          setScrollDialog({
                            show: true,
                            fieldLabel: item.field_label,
                            links: links,
                            fieldType,
                          })
                        }
                        sx={{
                          marginLeft: "5px",
                          cursor: "pointer",
                        }}
                      />
                    </MuiTooltip>
                  </MuiTypography>
                </MuiGrid>
              )}
            </MuiGrid>
          ) : item.field_value && isDescriptive ? (
            // With the help of isDescriptive flag we will be showing a divider line based on the formspec specification.
            <MuiGrid container size={12} key={item.field_id}>
              <MuiGrid size={12}>
                <MuiTypography variant="body2" sx={StyleApp.summaryLink}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.field_label, {
                        ALLOWED_TAGS: AllowedTags,
                      }),
                    }}
                  />
                </MuiTypography>
              </MuiGrid>
            </MuiGrid>
          ) : null;
        })}
      </MuiGrid>
    </Section>
  ));
};

const getFormattedValue = (inputValue, fieldType) => {
  if (fieldType === "percent") return `${parseFloat(inputValue)}%`;
  else if (fieldType === "money") {
    return `${Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    })
      .format(String(inputValue))
      .replace(/(\.|,)00$/g, "")}`;
  }
  return inputValue;
};
